import { debounce } from "lodash";
import { useEffect } from "react";
import { Controller, DeepPartial, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import InputLabel from "@/atoms-react/input/Input.react";
import { RadioCard } from "@/atoms-react/radio-card/index.react";
import { Label } from "@/atoms-react/text/Text.react";
import { authenticatedCompanyFormType, companyDetails } from "@/features/company/types";
import { LegalDocument } from "@/features/user/document/types";
import { formatCurrencyInput, formatNumber } from "@/features/utils/functions";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { UploadDocumentInput } from "@/molecules/upload-document-input";
import { formStyle } from "../form-style";
import { buildDefaultValuesFromCompany } from "./utils";

type AuthenticatedCompanyFormProps = React.ComponentPropsWithoutRef<"div"> & {
  editCompanyData: (companyData: authenticatedCompanyFormType) => void;
  company: companyDetails;
  isAdmin: boolean;
  actionEnabled: boolean;
};

export const AuthenticatedCompanyForm = ({
  editCompanyData,
  company,
  isAdmin = false,
  actionEnabled
}: AuthenticatedCompanyFormProps) => {
  const translation = useTranslation(["pass", "atoms"]);
  const { t } = translation;
  const { getLanguage } = usei18n();

  const handleOnChange = debounce((data: authenticatedCompanyFormType) => {
    editCompanyData(data);
  }, 4000);

  // useForm - START //
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch
  } = useForm<authenticatedCompanyFormType>({
    defaultValues: buildDefaultValuesFromCompany(company.legalEntity),
    mode: "onChange"
  });

  // Mutation for level 3 edition will invalidate data, we catch that and reset form data
  useEffect(() => {
    reset(buildDefaultValuesFromCompany(company.legalEntity));
  }, [company]);

  useEffect(() => {
    const unPartialDocument = (
      document: DeepPartial<LegalDocument> | null | undefined
    ): LegalDocument | null => {
      if (!document) return null;
      if (!document.name || !document.base64docs) return null;

      return {
        name: document.name,
        fileFormat: document.fileFormat,
        base64docs: document.base64docs
      };
    };

    const subscription = watch((values, { type }) => {
      const description = watch("Comments");

      if (!type) return;
      if (description && (description?.trim()?.length < 20 || description?.trim()?.length > 160))
        return;
      handleOnChange({
        ShareCapital: values.ShareCapital ?? null,
        ActivityRequiredAgreement: values.ActivityRequiredAgreement ?? null,
        AmlObligations: values.AmlObligations ?? null,
        Comments: values.Comments ?? null,
        FiancialYearDocument: unPartialDocument(values.FiancialYearDocument),
        OrganizationalDocument: unPartialDocument(values.OrganizationalDocument),
        RbeDocument: unPartialDocument(values.RbeDocument),
        SecondIdentityDocument: unPartialDocument(values.SecondIdentityDocument),
        CompanyIban: values.CompanyIban ?? null
      });
    });

    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  // useForm - END //

  const uploadDocumentOnError = (messagekey: string) => {
    toast.error(t(messagekey));
  };

  return (
    <form>
      <div className={formStyle.container}>
        <div className={formStyle.containerLine}>
          <div className={formStyle.containerLineTitle}>
            <Label>{t("business.input.shareCapital")}</Label>
          </div>

          <div className={formStyle.containerLineContent}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  dataCy="pro-authenticated-form-input-shareCapital"
                  required={false}
                  disabled={
                    (!!company?.legalEntity?.UserValidation || actionEnabled === false) && !isAdmin
                  }
                  data-tooltip={t("business.input.shareCapital")}
                  suffixIcon={<span>€</span>}
                  error={errors.ShareCapital ? t("atoms:general.required") : ""}
                  value={value ? formatNumber(value) : undefined}
                  onChange={(value) => {
                    const formattedValue = formatCurrencyInput(value);
                    const parsed = parseFloat(formattedValue);
                    if (!isNaN(parsed)) {
                      onChange(parsed);
                    }
                  }}
                />
              )}
              name="ShareCapital"
            />
          </div>
        </div>

        <div className={formStyle.containerLine}>
          <div className={formStyle.containerLineTitle}>
            <Label>{t("business.input.activity_description_label")}</Label>
          </div>

          <div className={formStyle.containerLineContent}>
            <Controller
              control={control}
              rules={{
                maxLength: {
                  value: 160,
                  message: t("flow.id_control.input.activity_textarea.error.max_length")
                },
                minLength: {
                  value: 20,
                  message: t("flow.id_control.input.activity_textarea.error.min_length")
                }
              }}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  dataCy="pro-authenticated-form-input-activityDescription"
                  data-tooltip={t("business.input.activity_description_label")}
                  disabled={
                    (!!company?.legalEntity?.UserValidation || actionEnabled === false) && !isAdmin
                  }
                  placeholder={""}
                  value={value ?? undefined}
                  required={false}
                  error={errors.Comments?.message}
                  rows={3}
                  onChange={(value) => {
                    onChange(value);
                  }}
                />
              )}
              name="Comments"
            />
          </div>
        </div>

        <div className={formStyle.containerLine}>
          <div className={formStyle.containerLineTitle}>
            <Label>{t("business.input.second_justificiation")}</Label>
          </div>
          <div className={formStyle.containerLineContent}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <UploadDocumentInput
                  dataCy="pro-authenticated-form-document-upload-companyIdentity"
                  disabled={
                    (!!company?.legalEntity?.UserValidation || actionEnabled === false) && !isAdmin
                  }
                  document={value}
                  onError={uploadDocumentOnError}
                  onChange={(data) => {
                    if (data.base64docs && data.fileFormat) {
                      data.base64docs = `data:${data.fileFormat};base64,${data.base64docs}`;
                      onChange(data);
                    }
                  }}
                ></UploadDocumentInput>
              )}
              name="SecondIdentityDocument"
            />
          </div>
        </div>

        <div className={formStyle.containerLine}>
          <div className={formStyle.containerLineTitle}>
            <Label>{t("business.input.rbe")}</Label>
          </div>
          <div className={formStyle.containerLineContent}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <UploadDocumentInput
                  dataCy="pro-authenticated-form-document-upload-rbe"
                  disabled={
                    (!!company?.legalEntity?.UserValidation || actionEnabled === false) && !isAdmin
                  }
                  document={value}
                  onError={uploadDocumentOnError}
                  onChange={(data) => {
                    if (data.base64docs && data.fileFormat) {
                      data.base64docs = `data:${data.fileFormat};base64,${data.base64docs}`;
                      onChange(data);
                    }
                  }}
                />
              )}
              name="RbeDocument"
            />
          </div>
        </div>

        <div className={formStyle.containerLine}>
          <div className={formStyle.containerLineTitle}>
            <Label>
              {company?.legalEntity?.ShareholderIsLegalEntity ? "* " : ""}
              {t("business.input.chart")}
            </Label>
          </div>
          <div className={formStyle.containerLineContent}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <UploadDocumentInput
                  dataCy="pro-authenticated-form-document-upload-legalChart"
                  disabled={
                    (!!company?.legalEntity?.UserValidation || actionEnabled === false) && !isAdmin
                  }
                  document={value}
                  onError={uploadDocumentOnError}
                  onChange={(data) => {
                    if (data.base64docs && data.fileFormat) {
                      data.base64docs = `data:${data.fileFormat};base64,${data.base64docs}`;
                      onChange(data);
                    }
                  }}
                />
              )}
              name="OrganizationalDocument"
            />
          </div>
        </div>

        <div className={formStyle.containerLine}>
          <div className={formStyle.containerLineTitle}>
            <Label>{t("business.input.FiancialYearDocument")}</Label>
          </div>
          <div className={formStyle.containerLineContent}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <UploadDocumentInput
                  dataCy="pro-authenticated-form-document-upload-annualAccounts"
                  disabled={
                    (!!company?.legalEntity?.UserValidation || actionEnabled === false) && !isAdmin
                  }
                  document={value}
                  onError={uploadDocumentOnError}
                  onChange={(data) => {
                    if (data.base64docs && data.fileFormat) {
                      data.base64docs = `data:${data.fileFormat};base64,${data.base64docs}`;
                      onChange(data);
                    }
                  }}
                />
              )}
              name="FiancialYearDocument"
            />
          </div>
        </div>

        <div className={formStyle.containerLine}>
          <div className={formStyle.containerLineTitle}>
            <Label>{t("business.input.ActivityRequiredAgreement")} </Label>
          </div>

          <div className={twMerge(formStyle.containerLineContent, "flex gap-5")}>
            <Controller
              control={control}
              rules={{
                validate: (x) => x !== undefined
              }}
              name="ActivityRequiredAgreement"
              render={({ field: { onChange, value } }) => {
                return (
                  <>
                    <RadioCard
                      dataCy="pro-authenticated-form-radiocard-positiv-activityAuth"
                      active={value === true}
                      disabled={
                        (!!company?.legalEntity?.UserValidation || actionEnabled === false) &&
                        !isAdmin
                      }
                      onClick={() => onChange(true)}
                      className={twMerge(
                        "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                        errors.ActivityRequiredAgreement && "border-red-500"
                      )}
                    >
                      {getLanguage() === "fr" ? "Oui" : "Yes"}
                    </RadioCard>
                    <RadioCard
                      dataCy="pro-authenticated-form-radiocard-negativ-activityAuth"
                      disabled={
                        (!!company?.legalEntity?.UserValidation || actionEnabled === false) &&
                        !isAdmin
                      }
                      className={twMerge(
                        "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                        errors.ActivityRequiredAgreement ? "border-red-500" : ""
                      )}
                      active={value === false}
                      onClick={() => onChange(false)}
                    >
                      {getLanguage() === "fr" ? "Non" : "No"}
                    </RadioCard>
                  </>
                );
              }}
            />
          </div>
        </div>

        <div className={formStyle.containerLine}>
          <div className={formStyle.containerLineTitle}>
            <Label>{t("business.input.AmlObligations")} </Label>
          </div>

          <div className={twMerge(formStyle.containerLineContent, "flex gap-5")}>
            <Controller
              control={control}
              rules={{
                validate: (x) => x !== undefined
              }}
              name="AmlObligations"
              render={({ field: { onChange, value } }) => (
                <>
                  <RadioCard
                    dataCy="pro-authenticated-form-radiocard-positiv-antiMoney"
                    disabled={
                      (!!company?.legalEntity?.UserValidation || actionEnabled === false) &&
                      !isAdmin
                    }
                    active={value === true}
                    onClick={() => onChange(true)}
                    className={twMerge(
                      "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                      errors.AmlObligations && "border-red-500"
                    )}
                  >
                    {getLanguage() === "fr" ? "Oui" : "Yes"}
                  </RadioCard>
                  <RadioCard
                    dataCy="pro-authenticated-form-radiocard-negativ-antiMoney"
                    disabled={
                      (!!company?.legalEntity?.UserValidation || actionEnabled === false) &&
                      !isAdmin
                    }
                    className={twMerge(
                      "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                      errors.AmlObligations ? "border-red-500" : ""
                    )}
                    active={value === false}
                    onClick={() => onChange(false)}
                  >
                    {getLanguage() === "fr" ? "Non" : "No"}
                  </RadioCard>
                </>
              )}
            />
          </div>
        </div>

        <div className={formStyle.containerLine}>
          <div className={formStyle.containerLineTitle}>
            <Label>{t("business.input.iban")}</Label>
          </div>

          <div className={formStyle.containerLineContent}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  dataCy="pro-authenticated-form-input-companyIban"
                  disabled={
                    (!!company?.legalEntity?.UserValidation || actionEnabled === false) && !isAdmin
                  }
                  data-tooltip={t("business.input.activity_description_label")}
                  placeholder={"Ex : FR1234 0000 0000 00000 0000 000"}
                  value={value ?? undefined}
                  required={false}
                  onChange={(value) => {
                    onChange(value);
                  }}
                />
              )}
              name="CompanyIban"
            />
          </div>
        </div>
      </div>
    </form>
  );
};
